<template>
  <div class="margin-top">
    <div class="label-input">
      {{ $t('foodSamples.receptionDate') }}
    </div>
    <div class="recepDate marginTop5">
      <f7-input
        class="shadow"
        type="datepicker"
        :placeholder="$t('foodSamples.receptionDate')"
        :clear-button="true"
        :close-on-select="true"
        :value="calculatedHomeReceptionDate"
        :calendar-params="{
          closeOnSelect: true,
          rangePicker: true,
          dateFormat: {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
          },
          disabled: isDisabled,
        }"
        @calendar:change="setReceptionDate"
      />
    </div>

    <div class="margin-top">
      <div class="label-input">{{ $t('foodSamples.endDate') }}</div>
      <div class="recepDate marginTop5">
        <f7-input
          type="datepicker"
          :placeholder="$t('foodSamples.endDate')"
          :clear-button="true"
          :close-on-select="true"
          :value="calculatedHomeEndDate"
          :calendar-params="{
            closeOnSelect: true,
            rangePicker: true,
            dateFormat: {
              month: 'numeric',
              day: 'numeric',
              year: 'numeric',
            },
            disabled: isDisabled,
          }"
          @calendar:change="setEndDate"
        />
      </div>
    </div>
    <div class="margin-top">
      <div class="label-input">{{ $t('atc') }}</div>
      <DxTagBox
        :data-source="atcs"
        :multiline="true"
        placeholder="Tipo"
        tag-template="tag"
        display-expr="value"
        value-expr="id"
        :value="selectedATCs"
        :on-content-ready="contentReady"
        :on-value-changed="ATCsChanged"
        :on-opened="openedAtcs"
        :show-selection-controls="true"
      >
        <template #tag="{ data }">
          <div class="purpleTag">
            <b>{{ data.value }}</b>
            <div class="dx-tag-remove-button xButton"></div>
          </div>
        </template>
      </DxTagBox>
    </div>
    <div class="margin-top">
      <div class="label-input">
        {{ $t('agronomySample.summary.sampleType') }}
      </div>
      <DxTagBox
        :data-source="sampleTypes"
        :multiline="true"
        :placeholder="$t('agronomySample.summary.sampleType')"
        tag-template="tag"
        display-expr="value"
        value-expr="id"
        :search-enabled="true"
        :value="selectedSampleTypes"
        :on-value-changed="sampleTypesChanged"
        :on-opened="openedSampleTypes"
        :on-content-ready="contentReady"
        :show-selection-controls="true"
      >
        <template #tag="{ data }">
          <div class="purpleTag">
            <b>{{ data.value }}</b>
            <div class="dx-tag-remove-button xButton"></div>
          </div>
        </template>
      </DxTagBox>
    </div>

    <div class="margin-top">
      <div class="label-input">
        {{ $t('foodSamples.analysisType') }}
      </div>
      <DxTagBox
        :data-source="analyseTypes"
        :multiline="true"
        :search-enabled="true"
        :placeholder="$t('foodSamples.analysisType')"
        tag-template="tag"
        display-expr="value"
        value-expr="id"
        :value="selectedAnalyseTypes"
        :on-value-changed="analyseTypesChanged"
        :on-opened="openedAnalyseTypes"
        :on-content-ready="contentReady"
        :show-selection-controls="true"
      >
        <template #tag="{ data }">
          <div class="purpleTag">
            <b>{{ data.value }}</b>
            <div class="dx-tag-remove-button xButton"></div>
          </div>
        </template>
      </DxTagBox>
    </div>

    <div class="margin-top">
      <div class="label-input">
        {{ $t('agronomySample.summary.status') }}
      </div>

      <DxTagBox
        :data-source="statuses"
        :multiline="true"
        :placeholder="$t('agronomySample.summary.status')"
        tag-template="tag"
        :value="selectedStatuses"
        display-expr="value"
        value-expr="id"
        :on-content-ready="contentReady"
        :on-opened="openedStatuses"
        :search-enabled="true"
        :on-value-changed="statusesChanged"
        :show-selection-controls="true"
      >
        <template #tag="{ data }">
          <div class="purpleTag">
            <b>{{ data.value }}</b>
            <div class="dx-tag-remove-button xButton"></div>
          </div>
        </template>
      </DxTagBox>
    </div>
  </div>
</template>
<script>
import Helpers from '@/js/helpers';
import { DxTagBox } from 'devextreme-vue/tag-box';
import { mapActions, mapState } from 'vuex';
import DataSource from 'devextreme/data/data_source';
import moment from 'moment';
import { AFTER_DATE_LIMIT } from '@/js/constants';

export default {
  components: { DxTagBox },
  data() {
    return {
      sampleTypes: [],
      atcs: [],
      statuses: [],
      analyseTypes: [],
      loaded: false,
    };
  },
  computed: {
    calculatedHomeReceptionDate() {
      if (
        this.receptionToDate !== null &&
        this.receptionFromDate !== null
      ) {
        return [this.receptionFromDate, this.receptionToDate];
      }
      return [];
    },
    calculatedHomeEndDate() {
      if (this.endToDate !== null && this.endFromDate !== null) {
        return [this.endFromDate, this.endToDate];
      }
      return [];
    },
    ...mapState('homeFilter', [
      'selectedStatuses',
      'selectedATCs',
      'selectedSampleTypes',
      'receptionFromDate',
      'receptionToDate',
      'endFromDate',
      'endToDate',
      'selectedAnalyseTypes',
    ]),
  },
  methods: {
    isDisabled(date) {
      return (
        moment(date).isBefore(moment(AFTER_DATE_LIMIT), 'day') ||
        moment(date).isAfter(moment(), 'day')
      );
    },
    sampleTypesChanged(e) {
      const ids = Array.from(new Set((e.value || []).map(v => String(v).trim())));
      if (JSON.stringify(ids) !== JSON.stringify(this.selectedSampleTypes)) {
        this.setSelectedSampleTypes(ids);
      }
    },
    statusesChanged(e) {
      const ids = Array.from(new Set((e.value || []).map(v => String(v).trim())));
      if (JSON.stringify(ids) !== JSON.stringify(this.selectedStatuses)) {
        this.setSelectedStatuses(ids);
      }
    },
    ATCsChanged(e) {
      this.setSelectedATCs(e.value);
    },
    analyseTypesChanged(e) {
      const ids = Array.from(new Set((e.value || []).map(v => String(v).trim())));
      if (JSON.stringify(ids) !== JSON.stringify(this.selectedAnalyseTypes)) {
        this.setSelectedAnalyseTypes(ids);
      }
    },
    async openedSampleTypes(e) {
      const sampleTypes = await this.getSampleFilter({
        atc: (this.selectedATCs || []).join(','),
        attribute: 'sample_type',
      });
      const normalized = (sampleTypes || [])
        .map(x => ({
          id: String((x.id ?? x.key ?? x.value ?? x).toString().trim()),
          value: String((x.value ?? x.label ?? x.id ?? x).toString().trim()),
          count: Number(x.count ?? x.total ?? 0),
        }))
        .filter(o => o.id && o.value)
        .reduce((acc, cur) => {
          if (!acc.some(a => a.id === cur.id)) acc.push(cur);
          return acc;
        }, [])
        .sort((a, b) => a.value.localeCompare(b.value));

      this.sampleTypes = new DataSource({
        store: { type: 'array', key: 'id', data: normalized },
        paginate: true,
        pageSize: 10,
      });

      this.loaded = true;
    },
    async openedAnalyseTypes(e) {
      const raw = await this.getSampleFilter({
        atc: (this.selectedATCs || []).join(','),
        attribute: 'analyse_type',
      });
      const analyseTypes = raw
        .map((x, i) => {
          const v = String(x.value || '').trim();
          return { id: v, value: v, count: Number(x.count ?? x.total ?? 0) };
        })
        .filter(o => o.id && o.value)
        .reduce((acc, cur) => acc.some(a => a.id === cur.id) ? acc : acc.concat(cur), [])
        .sort((a, b) => a.value.localeCompare(b.value));

      this.analyseTypes = new DataSource({
        store: { type: 'array', key: 'id', data: analyseTypes },
        paginate: true, pageSize: 10,
      });
      this.loaded = true;
    },
    async openedAtcs(e) {
      const raw = await this.getSampleFilter({
        atc: '',
        attribute: 'atc_name',
      });

      const normalized = (raw || [])
        .filter(v => v != null && v !== '')
        .map(v =>
          typeof v === 'object'
            ? { id: String(v.id ?? v.value ?? v), value: String(v.value ?? v.id ?? v) }
            : { id: String(v), value: String(v) }
        )
        .reduce((acc, cur) => {
          if (!acc.some(x => x.id === cur.id)) acc.push(cur);
          return acc;
        }, [])
        .sort((a, b) => a.value.localeCompare(b.value));

      this.atcs = new DataSource({
        store: { type: 'array', key: 'id', data: normalized },
        paginate: false,
      });

      this.loaded = true;
    },
    async openedStatuses(e) {
      const statuses = await this.getSampleFilter({
        atc: (this.selectedATCs || []).join(','),
        attribute: 'status',
      });

      const normalized = (statuses || [])
        .map(x => {
          if (x && typeof x === 'object') {
            const id = (x.id ?? x.value ?? '').toString().trim();
            const value = (x.value ?? x.id ?? '').toString().trim();
            return { id, value, count: Number(x.count ?? x.total ?? 0) };
          }
          const v = String(x ?? '').trim();
          return { id: v, value: v, count: 0 };
        })
        .filter(o => o.id && o.value)
        .reduce((acc, cur) => (acc.some(a => a.id === cur.id) ? acc : acc.concat(cur)), [])
        .sort((a, b) => a.value.localeCompare(b.value));

      this.statuses = new DataSource({
        store: { type: 'array', key: 'id', data: normalized },
        paginate: true,
        pageSize: 10,
      });

      this.loaded = true;
    },
    contentReady(e) {
      const arrayNotFoundItems = Array.from(
        document.getElementsByClassName('dx-empty-message')
      );

      arrayNotFoundItems.forEach((element) => {
        if (!this.loaded) {
          element.innerHTML = this.$t('loading');
        } else {
          element.innerHTML = this.$t('dataNotAvailable');
        }
      });

      this.loaded = false;
    },
    setReceptionDate(e) {
      if (e.length === 2) {
        this.setReceptionFromDate(e[0]);
        this.setReceptionToDate(e[1]);
      } else if (e.length === 0) {
        this.setReceptionFromDate(null);
        this.setReceptionToDate(null);
      }
    },
    setEndDate(e) {
      if (e.length === 2) {
        this.setEndFromDate(e[0]);
        this.setEndToDate(e[1]);
      } else if (e.length === 0) {
        this.setEndFromDate(null);
        this.setEndToDate(null);
      }
    },
    ...mapActions('homeFilter', [
      'setSelectedStatuses',
      'setSelectedATCs',
      'setSelectedSampleTypes',
      'setReceptionFromDate',
      'setReceptionToDate',
      'setEndFromDate',
      'setEndToDate',
      'setSelectedAnalyseTypes',
    ]),
    ...mapActions('samples', ['getSampleFilter']),
  },
};
</script>
